import { action } from 'typesafe-actions';

import { IDecision } from 'APIPackage/DecisionModule/interfaces/IDecision';
import { IDecisionParams } from 'APIPackage/DecisionModule/interfaces/IDecisionParams';
import { IKnmHistoryChanges } from 'KnmPackage/MainModule/interfaces/IKnmHistoryChanges';
import { KnmModalsEnum } from 'KnmPackage/MainModule/enums/KnmModalsEnum';
import { IKnmStatus } from 'APIPackage/CatalogsModule/interfaces/IKnmStatus';
import { IKnmOrderSelectOption } from 'KnmPackage/MainModule/interfaces/IKnmOrderSelectOption';
import { IDecisionOrganization } from 'APIPackage/DecisionModule/interfaces/IDecisionOrganization';
import { IKnmObject248 } from 'APIPackage/KnmModule/interfaces/IKnmObject248';
import { IKnmEvent248 } from 'APIPackage/KnmModule/interfaces/IKnmEvent248';
import { IKnmRequirementsNpa248 } from 'APIPackage/KnmModule/interfaces/IKnmRequirementsNpa248';
import { IKnmRequirementsNpa } from 'APIPackage/KnmModule/interfaces/IKnmRequirementsNpa';
import { IKnmLink } from 'APIPackage/KnmModule/interfaces/IKnmLink';
import { IErknmFormAgreement } from 'KnmErknmPackage/MainModule/interfaces/IErknmFormAgreement';
import { IErknmPrescribeCancel } from 'APIPackage/KnmModule/interfaces/IErknmPrescribeCancel';

import { DecisionActionsEnum } from '../enums/DecisionActionsEnum';
import { IDecisionFormRef } from '../interfaces/IDecisionFormRef';
import { IDecisionForm } from '../interfaces/IDecisionForm';
import { IDecisionErrorFieldWithProps } from '../interfaces/IDecisionErrorFieldWithProps';
import { IDecisionFormRenderProps } from '../components/DecisionForm';
import { IErknmStatusHintField } from '../../../KnmPackage/MainModule/interfaces/IErknmStatusHintField';

export const decisionCatalogsInit = () => action(DecisionActionsEnum.DECISION_CATALOGS_INIT);
export const decisionCreate = (data: IDecisionParams) => action(DecisionActionsEnum.DECISION_CREATE, { data });
export const decisionReset = () => action(DecisionActionsEnum.DECISION_RESET);
export const decisionReload = (callback?: () => void) => action(DecisionActionsEnum.DECISION_RELOAD, { callback });
export const decisionSetActionsDropdownVisible = (visible: boolean) =>
  action(DecisionActionsEnum.DECISION_SET_ACTIONS_DROPDOWN_VISIBLE, { visible });
export const decisionSetUrlId = (urlId: string) => action(DecisionActionsEnum.DECISION_SET_URL_ID, { urlId });
export const decisionSetRawData = (rawData: IDecision | undefined) =>
  action(DecisionActionsEnum.DECISION_SET_RAW_DATA, { rawData });
export const decisionSetFormRef = (formRef: IDecisionFormRef) =>
  action(DecisionActionsEnum.DECISION_SET_FORM_REF, { formRef });

export const decisionSetErrorFields = (errorFields: Array<IDecisionErrorFieldWithProps>) =>
  action(DecisionActionsEnum.DECISION_SET_ERROR_FIELDS, { errorFields });
export const decisionSignSubmitFail = (errors: IDecisionFormRenderProps['errors']) =>
  action(DecisionActionsEnum.DECISION_SIGN_SUBMIT_FAIL, { errors });

export const decisionSetOnSaveButtonClick = (onSaveButtonClick: () => void) =>
  action(DecisionActionsEnum.DECISION_SET_ON_SAVE_BUTTON_CLICK, { onSaveButtonClick });

export const decisionAddVisibleModal = (value: KnmModalsEnum, params?: { [key: string]: unknown }) =>
  action(DecisionActionsEnum.DECISION_ADD_VISIBLE_MODALS, { value, params });
export const decisionRemoveVisibleModal = (value: KnmModalsEnum) =>
  action(DecisionActionsEnum.DECISION_REMOVE_VISIBLE_MODALS, { value });

export const decisionSetInitialFormValues = (initialFormValues: IDecisionForm) =>
  action(DecisionActionsEnum.DECISION_SET_INITIAL_FORM_VALUES, { initialFormValues });
export const decisionPatchRawData = (partRawData: Partial<IDecision>) =>
  action(DecisionActionsEnum.DECISION_PATCH_RAW_DATA, { partRawData });
export const decisionResetInitialFormValues = () => action(DecisionActionsEnum.DECISION_RESET_INITIAL_FORM_VALUES);
export const decisionFillOrganizationsAndObjectsByOtherPassport = (
  value: {
    organizations: IDecisionOrganization[];
    objects: IKnmObject248[];
    events: IKnmEvent248[] | null;
    requirements: IKnmRequirementsNpa248[] | IKnmRequirementsNpa[] | null;
  },
  link: IKnmLink,
) =>
  action(DecisionActionsEnum.DECISION_FILL_ORGANIZATIONS_AND_OBJECTS_BY_OTHER_PASSPORT, {
    value,
    link,
  });

export const decisionGet = (id?: number) => action(DecisionActionsEnum.DECISION_GET, { id });
export const decisionGetRequest = () => action(DecisionActionsEnum.DECISION_GET_REQUEST);
export const decisionGetSuccess = (data: IDecision) => action(DecisionActionsEnum.DECISION_GET_SUCCESS, { data });
export const decisionGetFailure = () => action(DecisionActionsEnum.DECISION_GET_FAILURE);
export const decisionUpdate = (data: IDecisionParams) => action(DecisionActionsEnum.DECISION_UPDATE, { data });
export const decisionUpdateRequest = () => action(DecisionActionsEnum.DECISION_UPDATE_REQUEST);
export const decisionUpdateSuccess = (data: IDecision) => action(DecisionActionsEnum.DECISION_UPDATE_SUCCESS, { data });
export const decisionUpdateFailure = () => action(DecisionActionsEnum.DECISION_UPDATE_FAILURE);

export const decisionDraftSave = (data: IDecisionParams) => action(DecisionActionsEnum.DECISION_DRAFT_SAVE, { data });
export const decisionDraftDelete = (id: number, callback?: () => void, shouldReload?: boolean) =>
  action(DecisionActionsEnum.DECISION_DRAFT_DELETE, { id, callback, shouldReload });

export const decisionGetHistoryChanges = () => action(DecisionActionsEnum.DECISION_GET_HISTORY_CHANGES);
export const decisionSetHistoryChanges = (data: IKnmHistoryChanges) =>
  action(DecisionActionsEnum.DECISION_SET_HISTORY_CHANGES, { data });
export const decisionSetHistoryChangesIsLoading = (value: boolean) =>
  action(DecisionActionsEnum.DECISION_SET_HISTORY_CHANGES_IS_LOADING, { value });
export const decisionSetHistoryChangesIsOn = (value: boolean) =>
  action(DecisionActionsEnum.DECISION_SET_HISTORY_CHANGES_IS_ON, { value });

export const decisionGetSignedHistoryChanges = (startDateTime: string, endDateTime: string) =>
  action(DecisionActionsEnum.DECISION_GET_SIGNED_HISTORY_CHANGES, { startDateTime, endDateTime });
export const decisionSetSignedHistoryChanges = (data: IKnmHistoryChanges) =>
  action(DecisionActionsEnum.DECISION_SET_SIGNED_HISTORY_CHANGES, { data });

export const decisionDelete = () => action(DecisionActionsEnum.DECISION_DELETE);
export const decisionRestore = () => action(DecisionActionsEnum.DECISION_RESTORE);
export const decisionRestoreToLatestVersion = () => action(DecisionActionsEnum.DECISION_RESTORE_LATEST_SIGNED_VERSION);
export const decisionSendAgreement = () => action(DecisionActionsEnum.DECISION_SEND_AGREEMENT);

export const decisionTryOpenSignModal = (
  actGuid?: string,
  prescriptionGuid?: string,
  isNotNeedSubmitForm?: boolean,
  onClickButtonSignBeforeInnerAction?: () => Promise<void>,
  abortCheckCertificate?: () => void,
) =>
  action(DecisionActionsEnum.DECISION_TRY_OPEN_SIGN_MODAL, {
    actGuid,
    prescriptionGuid,
    isNotNeedSubmitForm,
    onClickButtonSignBeforeInnerAction,
    abortCheckCertificate,
  });
export const decisionDownloadXml = () => action(DecisionActionsEnum.DECISION_DOWNLOAD_XML);
export const decisionAgreementTemplateDownload = (agreement: IErknmFormAgreement) =>
  action(DecisionActionsEnum.DECISION_AGREEMENT_TEMPLATE_DOWNLOAD, { agreement });
export const decisionSignUploadModalApply = () => action(DecisionActionsEnum.DECISION_SIGN_UPLOAD_MODAL_APPLY);
export const decisionDeleteSign = () => action(DecisionActionsEnum.DECISION_DELETE_SIGN);
export const decisionSign = (
  certificate: string,
  id?: IDecision['id'],
  actGuid?: string,
  prescriptionGuid?: string,
  isCancelSign?: boolean,
) => action(DecisionActionsEnum.DECISION_SIGN, { certificate, id, actGuid, prescriptionGuid, isCancelSign });

export const decisionSetErknmKnoOrganizationFormValue = (
  knoOrganization: IDecisionForm['knoOrganization'] | undefined,
) => action(DecisionActionsEnum.DECISION_SET_KNO_ORGANIZATION_FORM_VALUE, { knoOrganization });
export const decisionSetErknmSupervisionTypeFormValue = (supervisionType: IDecisionForm['kindControl'] | undefined) =>
  action(DecisionActionsEnum.DECISION_SET_SUPERVISION_TYPE_FORM_VALUE, { supervisionType });
export const decisionSetErknmKnmKindVersionIdFormValue = (kindVersionId: string | undefined) =>
  action(DecisionActionsEnum.DECISION_SET_KIND_VERSION_ID_FORM_VALUE, { kindVersionId });
export const decisionSetReasonFormValue = (reasons: IDecisionForm['reasons'] | undefined) =>
  action(DecisionActionsEnum.DECISION_SET_REASONS_FORM_VALUE, { reasons });
export const decisionSetStatus = (status: IKnmStatus | undefined) =>
  action(DecisionActionsEnum.DECISION_SET_STATUS, { status });

export const decisionSetIsCanBeSigned = (data: boolean) =>
  action(DecisionActionsEnum.DECISION_SET_IS_CAN_BE_SIGNED, { data });
export const decisionGetOrders = (knoId: string, supervisionId: string, conditionId: string, typeId: string) =>
  action(DecisionActionsEnum.DECISION_GET_ORDERS, { knoId, supervisionId, conditionId, typeId });
export const decisionSetOrders = (knmOrders: IKnmOrderSelectOption[], conditionId: string) =>
  action(DecisionActionsEnum.DECISION_SET_ORDERS, { knmOrders, conditionId });
export const decisionSaveBeforeSignSuccess = (
  actGuid?: string,
  prescriptionGuid?: string,
  isNotNeedSubmitForm?: boolean,
  onClickButtonSignBeforeInnerAction?: () => Promise<void>,
  abortCheckCertificate?: () => void,
) =>
  action(DecisionActionsEnum.DECISION_SAVE_BEFORE_SING_SUCCESS, {
    actGuid,
    prescriptionGuid,
    isNotNeedSubmitForm,
    onClickButtonSignBeforeInnerAction,
    abortCheckCertificate,
  });
export const decisionSetLoading = (loading: boolean) => action(DecisionActionsEnum.DECISION_SET_LOADING, { loading });

export const decisionSetStatusHintText = (value: string | undefined) =>
  action(DecisionActionsEnum.DECISION_SET_STATUS_HINT_TEXT, { value });
export const decisionSetStatusHintFields = (fields: IErknmStatusHintField[]) =>
  action(DecisionActionsEnum.DECISION_SET_STATUS_HINT_FIELDS, { fields });
export const decisionErknmCancelPrescribe = (data: IErknmPrescribeCancel) =>
  action(DecisionActionsEnum.DECISION_ERKNM_CANCEL_PRESCRIBE, { data });
export const decisionSetPrescribeCancelSigned = (isSignComplete: boolean) =>
  action(DecisionActionsEnum.DECISION_SET_PRESCRIBE_SIGNED, { isSignComplete });

export const decisionSetExternalParams = (value: Record<string, string>) =>
  action(DecisionActionsEnum.DECISION_SET_EXTERNAL_PARAMS, { value });

export const decisionSetPrescriptionOldRules = (value: boolean | undefined) =>
  action(DecisionActionsEnum.DECISION_SET_PRESCRIPTION_OLD_RULES, { value });
export const decisionSetPrescriptionFailed = (value: boolean | undefined) =>
  action(DecisionActionsEnum.DECISION_SET_PRESCRIPTION_FAILED, { value });
export const decisionGetControlObjectsByNonErvkOrganization = (
  organizationSubjectCode: string,
  inn: string | null,
  ogrn: string | null,
  ogrnip: string | null,
  kpp: string | null,
  snils: string | null,
  rafpNza: string | null,
  organizationName: string | null,
  identityDocumentNumber: string | null,
) =>
  action(DecisionActionsEnum.DECISION_GET_CONTROL_OBJECTS_BY_NON_ERVK_ORGANIZATION, {
    organizationSubjectCode,
    inn,
    ogrn,
    ogrnip,
    kpp,
    snils,
    rafpNza,
    organizationName,
    identityDocumentNumber,
  });
